// src/components/map/utils/baseLayerFilter.ts
//
// The base well layer posts its OWN filter (EVA-35) — independent of the
// well-list filter — to the same `POST /api/v1/filter` endpoint the well
// list uses (see api/filter.ts's generateFilterQuery/getFilterFromQuery).
// Tiles then reference the returned filterId; map-svc resolves the viewport,
// org and entitled sources server-side, so the client never sends `?plays=`
// again on the tile endpoints. The resource-play dropdown is a separate
// route that does send `?bbox=` (EVA-35 viewport rework) and no longer takes
// this filterId at all — see fetchBaseWellPlays in api/map.ts.
import * as Sentry from "@sentry/react";

import { uid } from "utils/uid";

import { FilterAbortHolder, generateFilterQuery, getFilterFromQuery } from "api/filter";

import { EntityKind } from "models/entityKind";
import { IPredicate } from "models/filter";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

/** The categorical header field the base-layer resource-play filter narrows on. */
export const BASE_LAYER_RESOURCE_PLAY_PROPERTY = "Header.ResourcePlay";

/**
 * Builds the base layer's predicate list from the selected resource plays.
 * Zero predicates is a valid state and means "every well the org can see" —
 * an empty/undefined selection returns an empty array rather than a predicate
 * with an empty value.
 *
 * The shape mirrors the well-list filter's own categorical `in` predicate.
 *
 * `id` is REQUIRED by data-svc-rs: `Predicate.id` is a plain `String`, not an
 * `Option<String>` and with no serde default (`models/filter.rs`), so a
 * predicate without one fails deserialization outright —
 *
 *     missing field `id` at line 1 column 214
 *
 * — the POST 400s, `postBaseLayerFilter` returns undefined, the repost keeps
 * the previous filterId, and selecting a play does nothing at all. It is
 * silent because this caller suppresses the filtering-error toast (see
 * `baseLayerFilterAbortHolder`). Every other predicate builder in the app gets
 * away without carrying an `id` of its own — `getLegendFilter.ts` and
 * `buildShapefileBoundaryFilter` (shapefileFilterPredicate.ts) both build wire
 * predicates with no `id` — because they go through Redux, and
 * `filterSlice.ts`'s `addManyPropertiesToFilter` reducer (and
 * `facilityFilterSlice.ts`'s) stamps `pred.id = uid()` on every predicate
 * before it lands in `propertiesFilter`. This builder is the ONLY one that
 * bypasses Redux and posts straight through `generateFilterQuery`, which
 * stamps nothing — which is exactly why this predicate was missing an id.
 */
export function buildBaseLayerFilterPredicates(
  plays: Maybe<readonly string[]>
): IPredicate[] {
  if (!plays || plays.length === 0) return [];

  return [
    {
      type: "Property",
      property: BASE_LAYER_RESOURCE_PLAY_PROPERTY,
      operator: "in",
      value: [...plays],
      negatedOperator: false,
      pdenSource: PdenSourceEnum.Public,
      id: uid()
    }
  ];
}

// `getFilterFromQuery` defaults to a single module-level AbortController
// shared by every caller that doesn't pass its own. The base layer reposts
// independently of, and on different triggers than, those other callers
// (well-list filter, geo-calc/geo-sim job filters) — sharing that slot would
// let one cancel the other's request whenever they happened to overlap,
// silently dropping whichever one lost the race. This holder gives the base
// layer its own single-flight slot so only ITS OWN previous post can be
// cancelled by ITS OWN next one.
//
// It also suppresses `getFilterFromQuery`'s own filtering-error/backend-
// services toasts (see `suppressErrorToast` on FilterAbortHolder) for the
// same underlying reason: those toasts are right for a user-initiated post,
// but this one goes out unprompted on mount, org change and preference
// change, so a transient blip must not read as "an error occurred while
// filtering" to a user who hasn't touched the resource-play filter at all.
const baseLayerFilterAbortHolder: FilterAbortHolder = {
  current: null,
  suppressErrorToast: true
};

/**
 * Posts the base layer's filter and returns the new filterId, or undefined on
 * failure (the caller keeps whatever filterId it already has rather than
 * clearing it — a failed repost must not blank out a working layer).
 *
 * `previousFilterId` is chained through exactly like the well-list filter does
 * so the previous filter's TTL is shortened server-side.
 */
export async function postBaseLayerFilter(
  plays: Maybe<readonly string[]>,
  previousFilterId: Maybe<string>
): Promise<Maybe<string>> {
  const predicates = buildBaseLayerFilterPredicates(plays);
  const query = generateFilterQuery(
    predicates,
    null, // polygon — the base layer has no viewport/polygon predicate
    [], // excludePolygons
    [], // properties — no legend-derived child predicates
    previousFilterId ?? "",
    null, // screenshotPolygon
    EntityKind.Well,
    false // stampMapView — no polygon is sent, so nothing to stamp
  );

  const result = await getFilterFromQuery(query, baseLayerFilterAbortHolder);
  if (!result.ok) {
    // A "cancelled" result is this holder's own single-flight slot aborting a
    // superseded in-flight post (e.g. an org switch or a preference change
    // landing while an earlier post is still out) — ordinary, expected
    // behaviour, not a failure. Reporting it would just be noise indistinguishable
    // from a genuine backend failure in Sentry.
    if ("error" in result && result.error === "cancelled") return undefined;
    // With the toast suppressed above, a failed post is otherwise invisible:
    // filterId stays whatever it was (possibly undefined, on the very first
    // post), getBaseWellLayerTileUrl then returns undefined, and the layer is
    // simply never created — no exception is thrown anywhere in that chain.
    // This is the one place that state is observable, so it's the one place
    // that reports it.
    Sentry.withScope((scope) => {
      scope.setContext("map_base_well_filter_post", {
        action: "post",
        hadPreviousFilterId: previousFilterId !== undefined && previousFilterId !== "",
        status: "failed"
      });
      scope.setLevel("warning");
      Sentry.captureMessage("Base well layer filter post failed");
    });
    return undefined;
  }
  return result.value;
}
